import { HttpClient } from '@adapters/HttpClient';
import { WalletsResponse } from '../models/Wallets';

export type WalletsAdvancedFilterValue = string | number | Array<string | number>;

export interface WalletsAdvancedFilter {
  column: string;
  operator: string;
  value?: WalletsAdvancedFilterValue;
}

export interface WalletsAdvancedFilters {
  filters: WalletsAdvancedFilter[];
  logic?: 'AND' | 'OR';
}

export interface WalletsFilters {
  page?: number;
  perPage?: number;
  advanced_filters?: WalletsAdvancedFilters;
  sorting?: {
    column: string;
    direction: 'asc' | 'desc';
  };
}

export type WalletsExportFilters = Pick<WalletsFilters, 'advanced_filters' | 'sorting'>;

export interface WalletsExportResponse {
  url_file: string;
}

const buildAdvancedFiltersParams = (advancedFilters: WalletsAdvancedFilters) => {
  const params: Record<string, string | number> = {};

  advancedFilters.filters.forEach((filter, index) => {
    params[`advanced_filters[filters][${index}][column]`] = filter.column;
    params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

    if (
      filter.value === undefined ||
      filter.value === null ||
      (Array.isArray(filter.value) && filter.value.length === 0)
    ) {
      return;
    }

    if (Array.isArray(filter.value)) {
      filter.value.forEach((value, valueIndex) => {
        params[`advanced_filters[filters][${index}][value][${valueIndex}]`] = value;
      });
      return;
    }

    params[`advanced_filters[filters][${index}][value]`] = filter.value;
  });

  if (advancedFilters.logic) {
    params['advanced_filters[logic]'] = advancedFilters.logic;
  }

  return params;
};

export class WalletsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getWallets(filters: WalletsFilters = {}): Promise<WalletsResponse> {
    const { page = 1, perPage = 25, advanced_filters, sorting } = filters;
    const sortColumn = sorting?.column ?? 'id';
    const sortDirection = sorting?.direction ?? 'desc';

    const params: Record<string, string | number> = {
      page,
      per_page: perPage,
      'sorting[column]': sortColumn,
      'sorting[direction]': sortDirection,
    };

    if (advanced_filters && advanced_filters.filters.length > 0) {
      Object.assign(
        params,
        buildAdvancedFiltersParams({
          logic: advanced_filters.logic ?? 'AND',
          filters: advanced_filters.filters,
        }),
      );
    }

    return await this.httpClient.get<WalletsResponse>('/wallets', params, {}, true);
  }

  /**
   * Exporta el listado de billeteras a Excel respetando filtros y ordenación.
   * POST /api/v1/wallets/export
   */
  async exportWallets(columns: string[], filters: WalletsExportFilters = {}): Promise<WalletsExportResponse> {
    const body: {
      columns: string[];
      advanced_filters?: WalletsAdvancedFilters;
      sorting?: WalletsFilters['sorting'];
    } = { columns };

    if (filters.advanced_filters && filters.advanced_filters.filters.length > 0) {
      body.advanced_filters = {
        logic: filters.advanced_filters.logic ?? 'AND',
        filters: filters.advanced_filters.filters,
      };
    }

    if (filters.sorting) {
      body.sorting = filters.sorting;
    }

    return await this.httpClient.post<WalletsExportResponse>('/wallets/export', body);
  }
}
